import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi, type Priority } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtCurrency, fmtRelative } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, SourceBadge, StatusPill } from "@/components/dash/ui";

const CAT_LABEL: Record<string, string> = {
  revenue: "Revenue", customer: "Customer", operations: "Operations",
  support: "Support", stock: "Stock", connector: "Connector",
};

export function PriorityRow({ p, isAdmin }: { p: Priority; isAdmin: boolean }) {
  const qc = useQueryClient();
  const track = useMutation({
    mutationFn: () => dashApi.createAction({
      dedup_key: p.key, category: p.category, severity: p.severity, title: p.title,
      detail: p.reason, source_code: p.source, entity_ref: p.entity_ref, origin: "priority",
    }),
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <li className="flex items-start gap-3 py-3">
      <StatusPill status={p.severity} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-100">{p.title}</span>
          <span className="rounded bg-ink-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">{CAT_LABEL[p.category] ?? p.category}</span>
          {p.source && <SourceBadge code={p.source} />}
        </div>
        <p className="mt-0.5 text-xs text-slate-400">{p.reason}</p>
        <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-slate-600">
          {p.impact_value > 0 && <span>Impact {fmtCurrency(p.impact_value)}</span>}
          {p.customer_value > 0 && <span>Customer value {fmtCurrency(p.customer_value)}</span>}
          {p.first_seen && <span>Seen {fmtRelative(p.first_seen)}</span>}
          <span>Score {Math.round(p.score)}</span>
        </div>
      </div>
      <div className="shrink-0">
        {p.action ? (
          <StatusPill status={p.action.status} label={`Tracked: ${p.action.status}`} />
        ) : isAdmin ? (
          <button type="button" onClick={() => track.mutate()} disabled={track.isPending}
            className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2.5 py-1 text-xs text-brand-200 hover:bg-brand-500/20 disabled:opacity-50">
            {track.isPending ? "Tracking…" : "Track"}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export default function PrioritiesPage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const q = useQuery({ queryKey: ["decisions-priorities"], queryFn: dashApi.decisionsPriorities });
  const counts = q.data?.counts;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Today's Priorities"
        description="What needs management attention right now — ranked by severity, business impact, customer value and recency. Tracking a priority creates an internal IRONDash action; it never writes to a source system."
        right={counts && (
          <div className="flex gap-2">
            {counts.critical > 0 && <StatusPill status="critical" label={`${counts.critical} critical`} />}
            {counts.warning > 0 && <StatusPill status="warning" label={`${counts.warning} warning`} />}
            {counts.tracked > 0 && <span className="rounded-full bg-ink-700 px-2 py-0.5 text-[11px] text-slate-400">{counts.tracked} tracked</span>}
          </div>
        )}
      />
      <Panel title={`Priority queue (${counts?.total ?? 0})`}>
        {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.priorities.length ? (
          <EmptyState>Nothing needs attention right now. No open issues across connectors, customers, stock or support.</EmptyState>
        ) : (
          <ul className="divide-y divide-ink-500/40">
            {q.data.priorities.map((p) => <PriorityRow key={p.key} p={p} isAdmin={isAdmin} />)}
          </ul>
        )}
      </Panel>
    </div>
  );
}
